<script setup lang="ts">
import VText from "@magnit/core/src/components/VText/VText.vue";
import VWrapper from "@magnit/core/src/components/VWrapper/VWrapper.vue";
import VIcon from "@magnit/icons/src/components/VIcon/VIcon.vue";
import ArrowIcon from "@magnit/icons/src/assets/icons/arrow-down-corner-small-24.svg";
import type {
  INavigationSubmenuEmits,
  INavigationSubmenuProps,
  ISubMenu,
  ISubMenuLink,
} from "./VNavigation.types";

const props = defineProps<INavigationSubmenuProps>();
const emit = defineEmits<INavigationSubmenuEmits>();

const onTitleClick = (event: Event, item: ISubMenu) => {
  const element = event.target as HTMLElement;
  const groupElement = element.closest(".pl-navigation-submenu__group");

  if (groupElement) {
    if (
      groupElement.classList.contains("pl-navigation-submenu__group_active")
    ) {
      emit("submenuClose", item);
    } else {
      emit("submenuOpen", item);
    }
    groupElement.classList.toggle("pl-navigation-submenu__group_active");
  }
};

const onClickLink = (e: Event, link: ISubMenuLink) => {
  emit("linkClick", link);
  if (typeof link.onClick === "function") {
    link.onClick(e, link);
  }
};
</script>

<template>
  <div class="pl-navigation-submenu">
    <div class="pl-navigation-submenu__container">
      <VWrapper :indent="{ xs: '12px', s: '20px', m: '32px' }">
        <div class="pl-navigation-submenu__links">
          <slot name="before-links" />
          <div
            v-for="(section, index) in props.menu"
            :key="section.title"
            :class="[
              'pl-navigation-submenu__group',
              {
                'pl-navigation-submenu__group_active': index === 0,
              },
            ]"
          >
            <div
              class="pl-navigation-submenu__title"
              @click="(e) => onTitleClick(e, section)"
            >
              <VText
                tag="h4"
                :font="{ xs: 'title-medium', ml: 'title-small' }"
                class="pl-navigation-submenu__title-text"
                >{{ section.title }}</VText
              >
              <VIcon class="pl-navigation-submenu__icon" color="primary">
                <ArrowIcon />
              </VIcon>
            </div>
            <div class="pl-navigation-submenu__items">
              <div
                v-for="link in section.links"
                :key="link.label"
                class="pl-navigation-submenu__item"
              >
                <VText
                  :href="link.url"
                  :font="{ xs: 'body-large-regular', ml: 'body-small-regular' }"
                  :rel="link.rel"
                  target="_blank"
                  tag="a"
                  color="link-default"
                  class="pl-navigation-submenu__item-link"
                  @click="(e: Event) => onClickLink(e, link)"
                >
                  {{ link.label }}
                </VText>
              </div>
            </div>
          </div>
          <slot name="after-links" />
        </div>
      </VWrapper>
    </div>
    <div class="pl-navigation-submenu__overlay" @click="emit('close')" />
  </div>
</template>

<style lang="postcss" scoped>
.pl-navigation-submenu {
  inset: 56px 0 0;
  overflow-y: auto;
  position: fixed;
  width: 100%;
  z-index: var(--pl-z-index-over);
}

.pl-navigation-submenu__overlay {
  background: rgb(18 18 18 / 44%);
  cursor: pointer;
  height: 100%;
  left: 0;
  position: absolute;
  top: 0;
  width: 100%;
}

.pl-navigation-submenu__container {
  background: var(--pl-text-invert-primary);
  position: relative;
  z-index: var(--pl-z-index-level1);
}

.pl-navigation-submenu__links {
  padding: 16px 0;
}

.pl-navigation-submenu__group {
  padding-bottom: 24px;
  overflow: hidden;
}

.pl-navigation-submenu__title {
  cursor: pointer;
  display: flex;
  justify-content: space-between;
}

.pl-navigation-submenu__icon {
  .pl-navigation-submenu & {
    transition: transform 0.2s;
  }

  .pl-navigation-submenu__group_active & {
    transform: rotate(180deg);
  }
}

.pl-navigation-submenu__items {
  display: none;

  .pl-navigation-submenu__group_active & {
    display: block;
  }
}

.pl-navigation-submenu__title-text {
  .pl-navigation-submenu & {
    margin-top: 16px;

    &:nth-child(1) {
      margin-top: 0;
    }
  }
}

.pl-navigation-submenu__item {
  margin-top: 12px;
}

.pl-navigation-submenu__item-link {
  .pl-navigation-submenu & {
    cursor: pointer;
    text-decoration: none;

    &:visited {
      color: var(--pl-text-link-visited);
    }
  }
}

@media (--pl-viewport-from-ml) {
  .pl-navigation-submenu__links {
    column-count: 3;
    column-gap: 20px;
    padding-bottom: 0;
  }

  .pl-navigation-submenu__group {
    page-break-inside: avoid;
    break-inside: avoid-column;
  }

  .pl-navigation-submenu__title {
    cursor: default;
  }

  .pl-navigation-submenu__items {
    display: block;
  }

  .pl-navigation-submenu__icon {
    .pl-navigation-submenu & {
      display: none;
    }
  }
}

@media (--pl-viewport-from-l) {
  .pl-navigation-submenu__links {
    column-gap: 24px;
  }
}

@media (--pl-viewport-from-xl) {
  .pl-navigation-submenu__links {
    column-count: 4;
  }
}
</style>
